import { useEffect } from 'react'
import {
  PayPalButtons,
  usePayPalScriptReducer,
  type PayPalButtonsComponentProps,
} from '@paypal/react-paypal-js'
import type {
  PayPalButtonCreateOrder,
  PayPalButtonOnApprove,
  PayPalButtonOnClick,
  PayPalButtonOnError,
  PayPalButtonOnCancel,
  PayPalButtonOnInit,
} from '@paypal/paypal-js'
import { ErrorBoundary } from 'react-error-boundary'
import { logger } from '../../services/logger'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import Button from 'mixtiles-web-common/ui/Button'
import { useTranslation } from 'react-i18next'
import * as S from './PayPalButtonWrapper.styles'

interface PayPalButtonWrapperProps {
  createOrder: () => Promise<string>
  onApprove: () => void | Promise<void>
  style: PayPalButtonsComponentProps['style']
  disabled: boolean
  onDisabledClick: () => void
}

export default function PayPalButtonWrapper({
  createOrder,
  onApprove,
  style,
  disabled,
  onDisabledClick,
}: PayPalButtonWrapperProps) {
  const [{ isPending, isRejected }] = usePayPalScriptReducer()
  const { t } = useTranslation()

  // Cypress test override: @paypal/react-paypal-js renders PayPalButtons in an
  // iframe and gates rendering on the PayPal SDK script resolving against
  // paypal.com — which is unreliable from the CI runner and causes the
  // container's data-testid to never appear within the spec timeout. When the
  // test harness sets window.__MIXTILES_FAKE_PAYMENT_BUTTON__ (same flag as
  // the AP/GP stub in StripePaymentRequestForm), we swap the SDK-driven
  // button for a plain button so the wrapper, container, and disabled overlay
  // are reachable from Cypress. Gated on non-production so the flag has no
  // effect in a production bundle.
  const useFakeButton =
    window?.KEYS?.runtimeEnv !== 'production' &&
    window?.__MIXTILES_FAKE_PAYMENT_BUTTON__ === true

  useEffect(() => {
    if (isRejected) {
      analytics.track(EVENT_NAMES.PAYPAL_NOT_RENDERED, {
        Reason: 'sdk_load_failed',
      })
      logger.warning('PayPal SDK failed to load')
    }
  }, [isRejected])

  // Loading state - show button with spinner
  if (!useFakeButton && isPending) {
    return (
      <Button loading disabled fullWidth>
        {t('order.checkout.confirm_button_text_gdpr')}
      </Button>
    )
  }

  // Failed to load - hide PayPal option
  if (!useFakeButton && isRejected) {
    return null
  }

  const handleOnInit: PayPalButtonOnInit = (_data, _actions) => {
    analytics.track(EVENT_NAMES.PAYPAL_BUTTON_RENDERED, {
      Disabled: disabled,
    })
  }

  const handleCreateOrder: PayPalButtonCreateOrder = async (data) => {
    const orderId = await createOrder()
    analytics.track(EVENT_NAMES.PAYPAL_ORDER_CREATED, {
      'Order ID': orderId,
      'Payment Source': data.paymentSource,
    })
    return orderId
  }

  const handleOnApprove: PayPalButtonOnApprove = async (data) => {
    analytics.track(EVENT_NAMES.PAYPAL_ORDER_APPROVED, {
      'Order ID': data.orderID,
      'Payer ID': data.payerID,
      'Payment ID': data.paymentID,
    })
    await onApprove()
  }

  const handleOnCancel: PayPalButtonOnCancel = (data) => {
    analytics.track(EVENT_NAMES.PAYPAL_ORDER_CANCELLED, {
      'Order ID': data.orderID,
    })
  }

  const handleOnClick: PayPalButtonOnClick = (_data, actions) => {
    analytics.track(EVENT_NAMES.PAYPAL_BUTTON_TAPPED, {
      Disabled: false,
    })
    return actions.resolve()
  }

  const handleOnError: PayPalButtonOnError = (err) => {
    analytics.track(EVENT_NAMES.PAYPAL_ERROR, {
      Error: err,
    })
    logger.error('PayPal button error', err)
  }

  const handleDisabledClick = () => {
    analytics.track(EVENT_NAMES.PAYPAL_BUTTON_TAPPED, {
      Disabled: true,
    })
    onDisabledClick()
  }

  const handleRenderError = (err: Error) => {
    analytics.track(EVENT_NAMES.PAYPAL_NOT_RENDERED, {
      Reason: 'render_error',
      Error: err?.message,
    })
    logger.warning('PayPal button failed to render', err)
  }

  return (
    <S.ButtonContainer data-testid="paypal-button-container">
      {useFakeButton ? (
        <button
          type="button"
          data-testid="paypal-button-stub"
          style={{
            height: 52,
            borderRadius: 4,
            width: '100%',
            background: '#0070ba',
            color: '#fff',
            border: 'none',
          }}
        >
          PayPal
        </button>
      ) : (
        <ErrorBoundary fallback={null} onError={handleRenderError}>
          <PayPalButtons
            style={style}
            onInit={handleOnInit}
            createOrder={handleCreateOrder}
            onApprove={handleOnApprove}
            onCancel={handleOnCancel}
            onError={handleOnError}
            onClick={handleOnClick}
          />
        </ErrorBoundary>
      )}
      {disabled && (
        <S.ButtonOverlay
          data-testid="paypal-button-disabled-overlay"
          onClick={handleDisabledClick}
        />
      )}
    </S.ButtonContainer>
  )
}
